import { useState } from 'react';
import { useIntl } from 'react-intl';
import { Box, Notification } from '@packages/shared';
import { useConfig, useSession } from '@packages/utilities';
import { useAgnitasData } from '@packages/shared/src/hooks/useAgnitasData';
import { Sheet } from './Sheet';
import { subscribeSheetMessages } from './helpers/messages';
import { hasBeenClosed } from './helpers/closed';
import { DoiNotificationContent } from './DoiNotificationContent';
import { NewsletterSubscribeCompact } from './NewsletterSubscribeCompact';
import { useNlSheetTexts } from './helpers/useNlSheetTexts';

/**
 * The Newsletter Sheet Container Component:
 * A dialog at the bottom of the screen that is used to subscribe to the newsletter.
 *
 * It also checks your subscription state and hides itself if the users is already subscribed.
 * If the Confirmation is still pending, a notification is displayed to remind the user.
 */

const NewsletterSheet = () => {
  const { nlTexts } = useNlSheetTexts();
  const intl = useIntl();
  const config = useConfig();
  const { loggedIn, loading: loggingIn } = useSession();
  const closed = hasBeenClosed();

  const [forceHidden, setForceHidden] = useState(false);
  const [notification, setNotification] = useState<{
    show: boolean;
    message: string | JSX.Element;
  }>({ show: false, message: '' });

  const { useDOI } = config.forms.apiAgnitasUrl.values;

  const agnitasData = useAgnitasData((agnitasStatus) => {
    /**
     * Fire notification for logged in user if the Double Opt In E-Mail is still pending
     */
    if (agnitasStatus?.isPending && agnitasStatus.email && useDOI) {
      setNotification({
        message: (
          <DoiNotificationContent
            email={agnitasStatus.email}
            onClickedSuccess={() => setNotification({ ...notification, show: false })}
            onClickedError={(message) => setNotification({ message, show: true })}
          />
        ),
        show: true,
      });
    }
  });

  /**
   * Hide the sheet for a logged in customer if already subscribed (or pending)
   * or while agnitasData is not yet fetched
   * */
  const hideSheetLoggedIn =
    loggedIn && (agnitasData === null || agnitasData?.hasPermission || agnitasData?.isPending);

  /**
   * Hide the sheet during login process, if it has been closed,
   * or it should be hidden for a logged in customer
   * */
  const hideSheet = forceHidden || loggingIn || closed || hideSheetLoggedIn;

  return (
    <Box sx={{ position: 'relative' }}>
      <Notification
        open={notification.show}
        severity="warning"
        autoHideDuration={7000}
        onClose={() => setNotification({ ...notification, show: false })}
      >
        {notification.message}
      </Notification>

      {!hideSheet ? (
        <Sheet
          title={
            (nlTexts && nlTexts.label) || intl.formatMessage(subscribeSheetMessages.sheetTitle)
          }
          onClickClose={() => {
            setForceHidden(true);
          }}
        >
          <NewsletterSubscribeCompact />
        </Sheet>
      ) : (
        <div data-testid={closed || !loggingIn ? 'newslettersheet-closed' : undefined} />
      )}
    </Box>
  );
};

// eslint-disable-next-line import/no-default-export
export default NewsletterSheet;
